import { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SignaturePad from 'signature_pad';
import type { StrokePoint } from './xfdfSerializer';

const SIGNATURE_FONTS = [
  'Dancing Script',
  'Allura',
  'Alex Brush',
  'Yellowtail',
  'Petit Formal Script',
];

const GOOGLE_FONTS_URL =
  'https://fonts.googleapis.com/css2?' +
  SIGNATURE_FONTS.map((f) => `family=${f.replace(/ /g, '+')}`).join('&') +
  '&display=swap';

const FONTS_LINK_ID = 'signature-cursive-fonts';

function ensureFontsLoaded(): void {
  if (document.getElementById(FONTS_LINK_ID)) {
    return;
  }
  const link = document.createElement('link');
  link.id = FONTS_LINK_ID;
  link.rel = 'stylesheet';
  link.href = GOOGLE_FONTS_URL;
  document.head.appendChild(link);
}

const CANVAS_HEIGHT = 200;

interface SignaturePadModalProps {
  open: boolean;
  onClose: () => void;
  onDone: (data: SignatureResult) => void;
  userName: string;
  hasSavedSignature?: boolean;
  hasSavedInitials?: boolean;
  hideCheckbox?: boolean;
  initialTab?: number;
  mode?: 'signature' | 'initials';
}

export interface InkSignatureResult {
  type: 'ink';
  strokes: StrokePoint[][];
  canvasWidth: number;
  canvasHeight: number;
  saveSignature: boolean;
}

export interface TextSignatureResult {
  type: 'text';
  text: string;
  fontFamily: string;
  saveSignature: boolean;
}

export type SignatureResult = InkSignatureResult | TextSignatureResult;

const SignaturePadModal: FC<SignaturePadModalProps> = ({
  open,
  onClose,
  onDone,
  userName,
  hasSavedSignature = false,
  hasSavedInitials = false,
  hideCheckbox = false,
  initialTab = 0,
  mode = 'signature',
}) => {
  const [tab, setTab] = useState(initialTab);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const padRef = useRef<SignaturePad | null>(null);
  const listenerCleanupRef = useRef<(() => void) | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [typedName, setTypedName] = useState('');
  const [fontFamily, setFontFamily] = useState(SIGNATURE_FONTS[0]);
  const [canvasSize, setCanvasSize] = useState({ w: 400, h: 133 });
  const [saveSignature, setSaveSignature] = useState(false);

  const isInitialsMode = mode === 'initials';
  const hasSaved = isInitialsMode ? hasSavedInitials : hasSavedSignature;
  const maxLength = isInitialsMode ? 3 : undefined;
  const minLength = isInitialsMode ? 2 : 1;

  // Calculate initials from userName for initials mode
  const calculateInitials = useCallback((name: string): string => {
    if (!name) {
      return '';
    }
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return parts
      .map((part) => part.charAt(0).toUpperCase())
      .join('')
      .substring(0, 3);
  }, []);

  useEffect(() => {
    ensureFontsLoaded();
  }, []);

  const measureAndInit = useCallback(() => {
    if (!containerRef.current || !canvasRef.current) {
      return;
    }

    if (listenerCleanupRef.current) {
      listenerCleanupRef.current();
      listenerCleanupRef.current = null;
    }

    const w = containerRef.current.clientWidth;
    const h = CANVAS_HEIGHT;
    setCanvasSize({ w, h });

    const ratio = window.devicePixelRatio || 1;
    const canvas = canvasRef.current;
    canvas.width = w * ratio;
    canvas.height = h * ratio;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(ratio, ratio);
    }

    if (padRef.current) {
      padRef.current.off();
      padRef.current.clear();
    }

    const pad = new SignaturePad(canvas, {
      penColor: '#000',
      minWidth: 1.5,
      maxWidth: 1.5,
      backgroundColor: 'rgba(255,255,255,0)',
    });

    const handleEndStroke = (): void => {
      setIsEmpty(pad.isEmpty());
    };
    pad.addEventListener('endStroke', handleEndStroke);
    listenerCleanupRef.current = () => {
      pad.removeEventListener('endStroke', handleEndStroke);
    };

    padRef.current = pad;
    setIsEmpty(true);
  }, []);

  useEffect(() => {
    if (open && tab === 0) {
      const timer = setTimeout(measureAndInit, 100);
      return () => {
        clearTimeout(timer);
        if (listenerCleanupRef.current) {
          listenerCleanupRef.current();
          listenerCleanupRef.current = null;
        }
        if (padRef.current) {
          padRef.current.off();
        }
      };
    }
  }, [open, tab, measureAndInit]);

  useEffect(() => {
    if (open) {
      setTypedName(
        isInitialsMode ? calculateInitials(userName || '') : userName || ''
      );
      setIsEmpty(true);
      setTab(initialTab);
      setSaveSignature(hideCheckbox ? true : false);

      // Clear the canvas when modal reopens
      if (padRef.current) {
        padRef.current.clear();
      }
    }
  }, [
    open,
    userName,
    hideCheckbox,
    initialTab,
    isInitialsMode,
    calculateInitials,
  ]);

  useEffect(() => {
    if (!open && padRef.current) {
      padRef.current.off();
      padRef.current = null;
    }
  }, [open]);

  const handleClear = useCallback(() => {
    if (padRef.current) {
      padRef.current.clear();
      setIsEmpty(true);
    }
  }, []);

  const handleDone = useCallback(() => {
    if (tab === 0) {
      const pad = padRef.current;
      if (!pad || pad.isEmpty()) {
        return;
      }

      const rawData = pad.toData();
      const strokes: StrokePoint[][] = rawData.map((group) =>
        group.points.map((p) => ({ x: p.x, y: p.y }))
      );

      onDone({
        type: 'ink',
        strokes,
        canvasWidth: canvasSize.w,
        canvasHeight: canvasSize.h,
        saveSignature,
      });
    } else {
      const trimmed = typedName.trim();
      if (!trimmed || (isInitialsMode && trimmed.length < minLength)) {
        return;
      }
      onDone({
        type: 'text',
        text: trimmed,
        fontFamily,
        saveSignature,
      });
    }
  }, [
    tab,
    typedName,
    fontFamily,
    onDone,
    canvasSize,
    saveSignature,
    isInitialsMode,
    minLength,
  ]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isInitialsMode ? 'Create Initials' : 'Create Signature'}
      </DialogTitle>
      <DialogContent>
        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
          }}
          sx={{ mb: 2 }}
        >
          <Tab label="Draw" />
          <Tab label="Type" />
        </Tabs>

        {tab === 0 && (
          <Stack alignItems="center" gap={1}>
            <Box ref={containerRef} sx={{ width: '100%' }}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: '#fafafa',
                }}
              >
                <canvas
                  ref={canvasRef}
                  style={{ display: 'block', touchAction: 'none' }}
                />
              </Box>
            </Box>
            <Button size="small" onClick={handleClear} disabled={isEmpty}>
              Clear
            </Button>
          </Stack>
        )}

        {tab === 1 && (
          <Stack gap={2}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: -1 }}
            >
              Signature Style
            </Typography>
            <Select
              size="small"
              value={fontFamily}
              onChange={(e: SelectChangeEvent) => {
                setFontFamily(e.target.value);
              }}
              sx={{ maxWidth: 250 }}
              renderValue={(value) => {
                const index = SIGNATURE_FONTS.indexOf(value);
                return (
                  <span style={{ fontFamily: value }}>Style {index + 1}</span>
                );
              }}
            >
              {SIGNATURE_FONTS.map((font, index) => (
                <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                  Style {index + 1}
                </MenuItem>
              ))}
            </Select>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: -1 }}
            >
              {isInitialsMode ? 'Initials' : 'Name'}
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={typedName}
              onChange={(e) => {
                let value = e.target.value;
                if (maxLength && value.length > maxLength) {
                  value = value.slice(0, maxLength);
                }
                if (isInitialsMode) {
                  value = value.toUpperCase();
                }
                setTypedName(value);
              }}
              placeholder={
                isInitialsMode
                  ? 'Type your initials (2-3 characters)'
                  : 'Type your name'
              }
              inputProps={{
                maxLength: maxLength || undefined,
              }}
              helperText={
                isInitialsMode
                  ? `${typedName.length}/${maxLength} characters`
                  : undefined
              }
              error={
                isInitialsMode &&
                typedName.length > 0 &&
                typedName.length < minLength
              }
            />

            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                backgroundColor: '#fafafa',
                minHeight: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  fontFamily,
                  fontSize: isInitialsMode ? 48 : 36,
                  color: '#000',
                }}
              >
                {typedName || 'Preview'}
              </Typography>
            </Box>
          </Stack>
        )}
        {!hideCheckbox && (
          <FormControlLabel
            control={
              <Checkbox
                checked={saveSignature}
                onChange={(e) => {
                  setSaveSignature(e.target.checked);
                }}
                size="small"
              />
            }
            label={
              hasSaved
                ? `Replace existing ${
                    isInitialsMode ? 'initials' : 'signature'
                  }`
                : `Save ${isInitialsMode ? 'initials' : 'this signature'}`
            }
            sx={{ mt: 1 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleDone}
          disabled={
            tab === 0
              ? isEmpty
              : !typedName.trim() ||
                (isInitialsMode && typedName.trim().length < minLength)
          }
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SignaturePadModal;
